/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactNode, useEffect, useMemo } from 'react'
import { Column, PluginHook, TableOptions, usePagination, useTable } from 'react-table'
import { Pagination } from '~components/Pagination'
import { LoadingArea } from '~components/LoadingArea'
import TableGlobalFilter from './TableGlobalFilter'
import * as Styled from './Table.styles'
import { CELL_HEIGHT, HEAD_CELL_HEIGHT } from './Table.consts'
import DynamicRowsTableContainer from './DynamicRowsTableContainer'

export interface TableProps<D extends object = {}> {
  columns: Column<D>[]
  data: D[]
  options?: TableOptions<D>
  plugins?: PluginHook<D>[]
  globalSearchPlaceholder?: string
  title?: ReactNode | string
  isLoading?: boolean
  onResize?: (pageSize: number) => Promise<void>
  customPagination?: ReactNode
  noPagination?: boolean
  skipPageReset?: boolean
  hasDynamicRows?: boolean
  pageSize?: number
}

const Table = <D extends object = {}>({
  options,
  plugins: requestedPlugins = [],
  columns,
  data,
  globalSearchPlaceholder,
  title,
  isLoading,
  onResize,
  customPagination,
  noPagination,
  pageSize = 10,
  skipPageReset,
  hasDynamicRows = false,
}: TableProps<D>) => {
  const plugins = useMemo(() => {
    const newPlugins = [...requestedPlugins]

    if (!newPlugins?.includes(usePagination) && !customPagination && !noPagination) {
      newPlugins.push(usePagination)
    }

    return newPlugins
  }, [requestedPlugins, customPagination, noPagination])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,

    canPreviousPage,
    previousPage,
    canNextPage,
    nextPage,
    pageCount, // undefined if pagination hook wasn't provided to plugins
    gotoPage,

    page,
    rows,
    setGlobalFilter,
    setPageSize,
    state: { pageIndex, globalFilter },
  } = useTable<D>(
    {
      ...options,
      columns,
      data,
      autoResetGlobalFilter: false,
      autoResetPage: !skipPageReset,
      initialState: {
        pageSize,
      },
    },
    ...plugins,
  )

  useEffect(() => {
    if (!hasDynamicRows) {
      setPageSize?.(pageSize)
    }
  }, [pageSize, hasDynamicRows])

  const useDefaultOffsetPagination = !noPagination && !customPagination && pageCount

  const handleRowsCountChange = (newPageSize: number) => {
    setPageSize?.(newPageSize)
    onResize?.(newPageSize)
  }

  const handlePageChange = (pageNo: number) => {
    gotoPage(pageNo - 1)
  }

  return (
    <>
      {(title || setGlobalFilter) && (
        <Styled.TableHeader>
          {title}
          {setGlobalFilter && (
            <Styled.SearchRow>
              <TableGlobalFilter<D>
                isDisabled={!!isLoading}
                globalFilter={globalFilter}
                placeholder={globalSearchPlaceholder}
                onSetGlobalFilter={setGlobalFilter}
              />
            </Styled.SearchRow>
          )}
        </Styled.TableHeader>
      )}
      <DynamicRowsTableContainer isDynamic={hasDynamicRows} onRowsCount={handleRowsCountChange} cellHeight={CELL_HEIGHT} topOffset={HEAD_CELL_HEIGHT}>
        <LoadingArea isLoading={!!isLoading} placeholder="Loading...">
          <Styled.Table {...getTableProps()}>
            <Styled.THead>
              {headerGroups.map(headerGroup => (
                /* eslint-disable-next-line  react/jsx-key */
                <Styled.Tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    /* eslint-disable-next-line  react/jsx-key */
                    <Styled.Th {...column.getHeaderProps()}>{column.render('Header')}</Styled.Th>
                  ))}
                </Styled.Tr>
              ))}
            </Styled.THead>
            <Styled.TBody {...getTableBodyProps()}>
              {(page || rows).map(row => {
                prepareRow(row)

                return (
                  /* eslint-disable-next-line  react/jsx-key */
                  <Styled.Tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      /* eslint-disable-next-line  react/jsx-key */
                      <Styled.Td {...cell.getCellProps()}>{cell.render('Cell')}</Styled.Td>
                    ))}
                  </Styled.Tr>
                )
              })}
            </Styled.TBody>
          </Styled.Table>
        </LoadingArea>
      </DynamicRowsTableContainer>
      {useDefaultOffsetPagination > 0 && (
        <Styled.PaginationContainer>
          <Pagination total={pageCount} page={pageIndex + 1} onChange={handlePageChange} />
        </Styled.PaginationContainer>
      )}
      {!useDefaultOffsetPagination && customPagination && <Styled.PaginationContainer>{customPagination}</Styled.PaginationContainer>}
    </>
  )
}

Table.displayName = 'Table'
export default Table
